import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon } from '@/components/icons';
import { Pencil, Eye } from 'lucide-react';
import { useStrategicPillars, useStrategicPillarStats, useDeleteStrategicPillar, useUpdateStrategicPillarStatus, useSearchStrategicPillars } from '@/lib/api/hooks/strategic-pillar';
import { showToast } from '@/lib/toast';
import { Tab, Button, ActionDropdown, TanStackTable, type ColumnDef,  DeleteModal, PublishModal, UnpublishModal,  type TabItem, type DropdownAction, Pagination } from '@/components/ui';
import type { StrategicPillar as StrategicPillarType } from '@/lib/api/types';
import StrategicPillarsStats from './StrategicPillarsStats';
import formatDate from '@/utils/helpers/formatDate';
import { initialsCase } from '@/utils/helpers/textTransform';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useStrategicPillarsFilterSections } from '@/pages/articles-pages/strategic-pillars/FilterModule/FILTER_SECTIONS';
import { Paragraph } from '@/components/ui/Typography';

const StrategicPillars: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState<{ id: string; title: string } | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatusUpdate, setSelectedStatusUpdate] = useState<{ id: string; title: string; currentStatus: 'DRAFT' | 'PUBLISH'; newStatus: 'DRAFT' | 'PUBLISH' } | null>(null);


  // Base filter groups options state
  // Note: landingPagePrioritiesOfAfsa is now managed via WebPages section

  const { data: statsData } = useStrategicPillarStats();
  const stats = statsData || { total: 0, totalPublished: 0, totalDrafts: 0 };


  const FILTER_SECTIONS = useStrategicPillarsFilterSections();
            // ── URL-driven search / filter state ──────────────────────────────────────
            const {
              mode,
              searchTerm,
              filterState,
              page,
              size,
              setSearch,
              setFilters,
              removeFilter,
              clearFilters,
              setPage,
              apiFilters,
              getActiveFilterCount
            } = useSearchFilterParams(FILTER_SECTIONS)
          
        
        
          // Search API (when searching)
          const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchStrategicPillars(
            { page, size },
          searchTerm,
            mode === 'search' 
          )
        
          // Filter API (when filtering or default)
          const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useStrategicPillars(
            { page, size },
            {   status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT', ...apiFilters },
            mode === 'filter' || mode === 'default'
          )
        
          // Select appropriate data source: search takes priority
          const aggregateData = mode === 'search' ? searchData : filterData
          const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
          const isError = mode === 'search' ? isSearchError : isFilterError
        
          const totalPages = aggregateData?.data?.totalPage ?? 1
          const activeFilterCount = getActiveFilterCount()
          const refetchData = mode === 'search' ? refetchSearch : refetchFilter
          const emptyMessage = mode === 'search'
            ? 'No strategic pillars found matching your search criteria.'
            : mode === "filter" ?  'No strategic pillars found matching your filter criteria.'
            : 'No strategic pillars found.'
  
    const pillars = aggregateData?.data?.data || [];

 
  const { mutate: deletePillar, isPending: isDeleting } = useDeleteStrategicPillar();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateStrategicPillarStatus();

  const publishedCount = stats.totalPublished;
  const draftsCount = stats.totalDrafts;


  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];


  const handleEdit = useCallback((pillarId: string) => {
    navigate(`/admin/strategic-pillars/${pillarId}/edit`);
  }, [navigate]);


  const handleDeleteClick = useCallback((pillar: { strategicPillarId: string; title: string }) => {
    setSelectedPillar({ id: pillar.strategicPillarId, title: pillar.title });
    setDeleteModalOpen(true);
  }, []);

  // Handle status update click
  const handleStatusUpdateClick = useCallback((pillar: StrategicPillarType) => {
    const newStatus = pillar.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
    setSelectedStatusUpdate({
      id: pillar.strategicPillarId,
      title: pillar.title,
      currentStatus: pillar.status,
      newStatus
    });
    setStatusModalOpen(true);
  }, []);


  const confirmDelete = () => {
    if (!selectedPillar) return;

    deletePillar(selectedPillar.id, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Strategic pillar deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedPillar(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  // Confirm status update
  const confirmStatusUpdate = () => {
    if (!selectedStatusUpdate) return;

    updateStatus(
      { strategicPillarId: selectedStatusUpdate.id, status: selectedStatusUpdate.newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              selectedStatusUpdate.newStatus === 'PUBLISH'
                ? 'Strategic pillar published successfully!'
                : 'Strategic pillar unpublished successfully!'
            );
            setStatusModalOpen(false);
            setSelectedStatusUpdate(null);
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };



  const getDropdownActions = useCallback((pillar: StrategicPillarType): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'Edit',
        onClick: (e) => {
          e.stopPropagation();
          handleEdit(pillar.strategicPillarId);
        }
      },
      {
        label: pillar.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          handleStatusUpdateClick(pillar);
        },
        disabled: isUpdatingStatus
      }
    ];

    // Only show Delete for draft articles
    if (pillar.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteClick(pillar);
        },
        variant: 'danger' as const,
        disabled: isDeleting
      });
    }

    return actions;
  }, [handleEdit, handleStatusUpdateClick, handleDeleteClick, isUpdatingStatus, isDeleting]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'PUBLISH':
        return { label: 'Published', dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'DRAFT':
        return { label: 'Draft', dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
      default:
        return { label: status, dot: 'bg-blue-400', badge: 'bg-blue-50 text-blue-700 border-blue-200' };
    }
  };

  const columns = useMemo<ColumnDef<StrategicPillarType, unknown>[]>(() => [
    {
      id: 'strategicPillar',
      header: 'Strategic Pillar',
      cell: ({ row }) => {
        const pillar = row.original;
        return (
          <div className="flex items-center gap-3">
            {pillar.coverImage ? (
              <img
                src={pillar.coverImage}
                alt={pillar.title}
                className="w-14 h-12 object-cover rounded-md flex-shrink-0 bg-gray-100"
              />
            ) : (
              <div className="w-14 h-12 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{pillar.title}</div>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4' },
    },
    {
      id: 'author',
      header: 'Author',
      cell: ({ row }) => {
        const pillar = row.original;
        const name = pillar.createdByUsername || '';
        const initials = initialsCase(name);
        return name ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{initials}</span>
            </div>
            <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{name}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: ({ row }) => {
        const pillar = row.original;
        const dateStr = (pillar as unknown as Record<string, string>).updatedAt || pillar.datePublished || pillar.createdAt;
        return dateStr ? (
          <div>
            <div className="text-sm text-gray-900 font-medium">{formatDate(dateStr, 'DD MMM YYYY')}</div>
            <div className="text-xs text-gray-400">{formatDate(dateStr, 'hh:mm A')}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const { label, dot, badge } = getStatusConfig(row.original.status);
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-medium ${badge}`}>
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            {label}
          </span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const pillar = row.original;
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Edit"
              onClick={(e) => { e.stopPropagation(); handleEdit(pillar.strategicPillarId); }}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Preview"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/strategic-pillars/${pillar.strategicPillarId}`); }}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <Eye className="w-4 h-4" />
            </button>
            <div onClick={(e) => e.stopPropagation()}>
              <ActionDropdown
                id={pillar.strategicPillarId}
                actions={getDropdownActions(pillar as unknown as StrategicPillarType)}
              />
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
  ], [getDropdownActions, handleEdit, navigate]);

  const handleRowClick = useCallback((row: StrategicPillarType) => navigate(`/admin/strategic-pillars/${row.strategicPillarId}`), [navigate]);

  const isEmptyState = !isFetching && mode === "default" && pillars.length === 0;


  return (
    <>
      {/* Stats Section */}
      <StrategicPillarsStats />
      
      <div className="space-y-6">

        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            onClick={() => navigate('/admin/strategic-pillars/new')}
          >
            Create New
          </Button>
        </div>

         {/* Search + Filter Controls */}
                  <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                    <div className="flex items-center relative gap-4 justify-between flex-wrap">
                      <Search
                        value={searchTerm}
                        onSearch={setSearch}
                        placeholder="Search ..."
                      />
                       <FilterModule
                            sections={FILTER_SECTIONS}
                            activeFilters={filterState}
                            onApply={setFilters}
                            activeFilterCount={activeFilterCount}
                          />
                    </div>
        
                    {/* Active filter chips — only shown in filter mode */}
                    <FilterParamsDisplay
                      sections={FILTER_SECTIONS}
                      filterState={filterState}
                      onRemove={removeFilter}
                      onClearAll={clearFilters}
                    />
                  </div>

        {/* Strategic Pillars Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative">

          {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        )  : isEmptyState  ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-post-icon.png" alt="No posts" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'published' ? 'No Recent Post' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'published' ? 'You are yet to create a post' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4" onClick={() => navigate('/admin/strategic-pillars/new')}>
                      Create New
                    </Button>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <TanStackTable
                    data={pillars}
                    columns={columns}
                    getRowId={(row) => row.strategicPillarId}
                    onRowClick={handleRowClick}
                    rowClassName="hover:bg-gray-50"
                    emptyMessage={emptyMessage}
                  />
                </div>
                     <Pagination   currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            className="p-4"/>
                </>
              )}
          
        </div>
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Strategic Pillar"
        subtitle="Are you sure you want to delete this strategic pillar?"
        itemName={selectedPillar?.title}
        warningText="This action cannot be undone. The strategic pillar will be permanently deleted."
        isLoading={isDeleting}
      />
      {selectedStatusUpdate && (
        <>
          {selectedStatusUpdate.newStatus === 'DRAFT' ? (
            <UnpublishModal
              isOpen={statusModalOpen}
              onClose={() => setStatusModalOpen(false)}
              onConfirm={confirmStatusUpdate}
              itemType="Post"
              isLoading={isUpdatingStatus}
            />
          ) : (
            <PublishModal
              isOpen={statusModalOpen}
              onClose={() => setStatusModalOpen(false)}
              onConfirm={confirmStatusUpdate}
              title="Publish Strategic Pillar"
              subtitle="Are you sure you want to publish this strategic pillar?"
              itemName={selectedStatusUpdate.title}
              infoText="This strategic pillar will be visible to the public once published."
              itemType="Strategic Pillar"
              isLoading={isUpdatingStatus}
            />
          )}
        </>
      )}
    </>
  );
};

export default StrategicPillars;
